import { Card, CardContent } from '@/components/ui/card';
import { calculateCapacityPercentage, formatBytes, getCapacityStatus } from '@/lib/utils';
import { DatabaseDetails } from '@/types/database';
import { AlertCircle, AlertTriangle, HardDrive } from 'lucide-react';

interface CapacityWarningProps {
  database: DatabaseDetails;
}

export default function CapacityWarning({ database }: CapacityWarningProps) {
  if (!database.capacity_exceeded) {
    return null;
  }

  const percentage = calculateCapacityPercentage(database.current_usage_bytes, database.capacity_limit_bytes);
  const status = getCapacityStatus(percentage);

  const getStatusColor = () => {
    switch (status) {
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-green-600 bg-green-50 border-green-200';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'high':
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      default:
        return <HardDrive className="h-5 w-5 text-yellow-600" />;
    }
  };

  return (
    <Card className={`border-2 ${getStatusColor()} mb-4`}>
      <CardContent className="p-4">
        <div className="flex items-start space-x-3">
          {getStatusIcon()}
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-2">
              Storage Capacity Exceeded
            </h3>
            <div className="space-y-2 text-sm">
              <p>
                Database <strong>{database.dbname}</strong> has exceeded its storage capacity limit.
              </p>
              <div className="flex items-center space-x-4">
                <span>
                  <strong>Current Usage:</strong> {formatBytes(database.current_usage_bytes)}
                </span>
                <span>
                  <strong>Capacity Limit:</strong> {formatBytes(database.capacity_limit_bytes)}
                </span>
                <span>
                  <strong>Usage:</strong> {percentage}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${
                    status === 'critical' ? 'bg-red-500' :
                    status === 'high' ? 'bg-orange-500' :
                    status === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
              <div className="mt-3 p-3 bg-white rounded border">
                <p className="font-medium text-red-700 mb-1">⚠️ Important Notice:</p>
                <ul className="text-sm space-y-1">
                  <li>• SA and RW users have been automatically made read-only</li>
                  <li>• No new data can be written or updated</li>
                  <li>• Only read operations are allowed</li>
                  <li>• Consider increasing database capacity or cleaning up data</li>
                </ul>
              </div>
              {database.capacity_exceeded_at && (
                <p className="text-xs text-gray-600 mt-2">
                  Capacity exceeded on: {new Date(database.capacity_exceeded_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 